import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function getAnchorPosition(anchorEl) {
  const rect = anchorEl.getBoundingClientRect();
  return {
    top: rect.bottom + 4,
    left: rect.right,
  };
}

export function Menu({ open, onClose, children, className = '', anchorRef }) {
  const ref = useRef(null);
  const usePortal = Boolean(anchorRef);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      const menuNode = ref.current;
      const anchorNode = anchorRef?.current;
      if (menuNode?.contains(e.target) || anchorNode?.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose, anchorRef]);

  useEffect(() => {
    if (!open || !usePortal) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      if (anchorRef?.current) {
        setPosition(getAnchorPosition(anchorRef.current));
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, usePortal, anchorRef]);

  if (!open) return null;

  const anchorEl = usePortal ? anchorRef?.current : null;
  const anchoredPosition = position || (anchorEl ? getAnchorPosition(anchorEl) : null);
  if (usePortal && !anchoredPosition) return null;

  const menuClassName = ['ds-menu', usePortal ? 'ds-menu--anchored' : '', className]
    .filter(Boolean)
    .join(' ');

  const menuStyle = usePortal
    ? {
        top: `${anchoredPosition.top}px`,
        left: `${anchoredPosition.left}px`,
      }
    : undefined;

  const menu = (
    <div className={menuClassName} ref={ref} role="menu" style={menuStyle}>
      {children}
    </div>
  );

  if (usePortal) {
    return createPortal(menu, document.body);
  }

  return menu;
}

export function MenuItem({ children, onClick, className = '' }) {
  return (
    <button type="button" className={`ds-menu__item ${className}`.trim()} role="menuitem" onClick={onClick}>
      {children}
    </button>
  );
}

export default Menu;
